import { useState, useEffect } from 'react';

export interface YouTubeVideo {
  id: string;
  videoId: string;
  title: string;
  thumbnail: string;
  link: string;
  publishedDate: string;
  author: string;
}

interface Rss2JsonItem {
  title: string;
  link: string;
  thumbnail: string;
  pubDate: string;
  author: string;
  guid: string;
}

interface Rss2JsonResponse {
  status: string;
  items: Rss2JsonItem[];
}

export function useLatestVideos(count: number = 3) {
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        setError(null);

        const rssUrl = encodeURIComponent(
          'https://www.youtube.com/feeds/videos.xml?channel_id=UCB0x981YRoqYauGR71yej6Q'
        );
        const response = await fetch(
          `https://api.rss2json.com/v1/api.json?rss_url=${rssUrl}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }

        const data: Rss2JsonResponse = await response.json();

        if (data.status !== 'ok' || !Array.isArray(data.items)) {
          throw new Error('Invalid response format');
        }

        const latestVideos = data.items
          .slice(0, count)
          .map((item) => {
            const match = item.link.match(/[?&]v=([^&]+)/);
            const videoId = match ? match[1] : '';

            return {
              id: videoId,
              videoId,
              title: item.title,
              thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
              link: item.link,
              publishedDate: item.pubDate,
              author: item.author,
            };
          })
          .filter((v) => v.videoId);

        setVideos(latestVideos);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load videos');
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [count]);

  return { videos, loading, error };
}